// @src/pre-deployment/src/components/TrackingService/services/TrackingV2Service.js v2.0
// - Migrated from src/pre-deployment/src/tracking/trackingService.js
// - Part of TrackingService component migration
// - Facade service combining all tracking services into unified API
// - Enhanced with ErrorHandling integration

import { doc, getDoc, setDoc } from 'firebase/firestore';

import { db } from '../../../services/firebase.js';

import { ContentDiscoveryService } from './ContentDiscoveryService.js';
import { LearningProgressService } from './LearningProgressService.js';
import { MasteryService } from './MasteryService.js';
import { silentTrack } from './SilentTrackService.js';

/**
 * TrackingV2Service - Unified facade for all tracking operations
 * Combines ContentDiscoveryService, LearningProgressService, and MasteryService
 * into a single convenient API for React components.
 */
export const TrackingV2Service = {
    /**
     * Initialize both documents with content discovery
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {Object} [dataParameters={}] - Additional parameters
     * @returns {Promise<boolean|null>} True on success, null on error
     */
    async initializeDocuments(userId, level, chapter, dataParameters = {}) {
        return silentTrack(async () => {
            console.log(`Initializing tracking documents for Level ${level} Chapter ${chapter}`);

            // Discover content
            const contentData = await ContentDiscoveryService.getContentMetadata(level, chapter);

            // Initialize both documents
            await LearningProgressService.initializeDocument(userId, level, chapter, dataParameters);
            await MasteryService.initializeDocument(userId, level, chapter, contentData);

            console.log(`Tracking documents initialized successfully`);
            return true;
        }, 'initialize_documents', {
            componentName: 'TrackingV2Service',
            severity: 'high',
            level: level,
            chapter: chapter,
            firebasePath: `users/${userId}/level_${level}/chapter_${chapter}/Learn`
        });
    },

    /**
     * Mark chapter as started
     */
    async markChapterStarted(userId, level, chapter, dataParameters = {}) {
        return LearningProgressService.markChapterStarted(userId, level, chapter, dataParameters);
    },

    /**
     * Mark chapter as completed
     */
    async markChapterCompleted(userId, level, chapter, dataParameters = {}) {
        return LearningProgressService.markChapterCompleted(userId, level, chapter, dataParameters);
    },

    /**
     * Unlock chapter
     */
    async unlockChapter(userId, level, chapter, dataParameters = {}) {
        return LearningProgressService.unlockChapter(userId, level, chapter, dataParameters);
    },

    /**
     * Update module progress
     */
    async updateModuleProgress(userId, level, chapter, moduleKey, progressData, dataParameters = {}) {
        return LearningProgressService.updateModuleProgress(userId, level, chapter, moduleKey, progressData, dataParameters);
    },

    /**
     * Add question attempt
     */
    async addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, questionData, dataParameters = {}) {
        return LearningProgressService.addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, questionData, dataParameters);
    },

    /**
     * Mark vocabulary exposed
     */
    async markVocabularyExposed(userId, level, chapter, wordId, moduleKey, dataParameters = {}) {
        return MasteryService.markVocabularyExposed(userId, level, chapter, wordId, moduleKey, dataParameters);
    },

    /**
     * Mark grammar exposed
     */
    async markGrammarExposed(userId, level, chapter, grammarId, moduleKey, dataParameters = {}) {
        return MasteryService.markGrammarExposed(userId, level, chapter, grammarId, moduleKey, dataParameters);
    },

    /**
     * Update vocabulary mastery
     */
    async updateVocabularyMastery(userId, level, chapter, wordId, moduleKey, masteryData, dataParameters = {}) {
        return MasteryService.updateVocabularyMastery(userId, level, chapter, wordId, moduleKey, masteryData, dataParameters);
    },

    /**
     * Update grammar mastery
     */
    async updateGrammarMastery(userId, level, chapter, grammarId, moduleKey, masteryData, dataParameters = {}) {
        return MasteryService.updateGrammarMastery(userId, level, chapter, grammarId, moduleKey, masteryData, dataParameters);
    },

    /**
     * Mark comprehension exposed
     */
    async markComprehensionExposed(userId, level, chapter, comprehensionId, moduleKey, dataParameters = {}) {
        return MasteryService.markComprehensionExposed(userId, level, chapter, comprehensionId, moduleKey, dataParameters);
    },

    /**
     * Update comprehension mastery
     */
    async updateComprehensionMastery(userId, level, chapter, comprehensionId, moduleKey, masteryData, dataParameters = {}) {
        return MasteryService.updateComprehensionMastery(userId, level, chapter, comprehensionId, moduleKey, masteryData, dataParameters);
    },

    /**
     * Mark dialogue exposed
     */
    async markDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters = {}) {
        return MasteryService.markDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
    },

    /**
     * Update dialogue mastery
     */
    async updateDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters = {}) {
        return MasteryService.updateDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
    },

    /**
     * Mark intro dialogue exposed
     */
    async markIntroDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters = {}) {
        return MasteryService.markIntroDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
    },

    /**
     * Update intro dialogue mastery
     */
    async updateIntroDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters = {}) {
        return MasteryService.updateIntroDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
    },

    /**
     * Mark conclusion dialogue exposed
     */
    async markConclusionDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters = {}) {
        return MasteryService.markConclusionDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
    },

    /**
     * Update conclusion dialogue mastery
     */
    async updateConclusionDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters = {}) {
        return MasteryService.updateConclusionDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
    },

    /**
     * Get chapter data (both documents)
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @returns {Promise<{learningProgress: Object|null, masteryData: Object|null}|null>} Combined chapter data
     */
    async getChapterData(userId, level, chapter) {
        return silentTrack(async () => {
            const [learningProgress, masteryData] = await Promise.all([
                LearningProgressService.getLearningProgress(userId, level, chapter),
                MasteryService.getMasteryData(userId, level, chapter)
            ]);

            return {
                learningProgress,
                masteryData
            };
        }, 'get_chapter_data', {
            componentName: 'TrackingV2Service',
            severity: 'medium',
            level: level,
            chapter: chapter,
            firebasePath: `users/${userId}/level_${level}/chapter_${chapter}/Learn`
        });
    },

    /**
     * Get Mastery data (wrapper for MasteryService.getMasteryData)
     */
    async getMasteryData(userId, level, chapter) {
        return MasteryService.getMasteryData(userId, level, chapter);
    },

    /**
     * Track Hangul character attempt (Korea University Level 0)
     * @param {Object} data - Hangul attempt data
     * @param {string} data.userId - User ID
     * @param {string} data.moduleId - Module identifier
     * @param {string} data.letter - Hangul letter attempted
     * @param {boolean} data.isCorrect - Whether attempt was correct
     * @param {string} data.timestamp - ISO timestamp of attempt
     * @returns {Promise<boolean|null>} True on success, null on error
     */
    async trackHangulAttempt(data) {
        return silentTrack(async () => {
            const { userId, moduleId, letter, isCorrect, timestamp } = data;

            const progressRef = doc(db,
                `feature_testing/Korea_University/Level0/HangulProgress/${userId}`
            );

            const progressSnap = await getDoc(progressRef);
            const existing = progressSnap.exists() ? progressSnap.data() : {
                modules: {},
                letters: {},
                totalAttempts: 0,
                totalCorrect: 0
            };

            // Update letter stats
            const letterStats = existing.letters[letter] || { attempts: 0, correct: 0 };
            letterStats.attempts++;
            if (isCorrect) letterStats.correct++;
            letterStats.lastPracticed = timestamp;

            // Update module stats
            const moduleStats = existing.modules[moduleId] || { attempts: 0, correct: 0 };
            moduleStats.attempts++;
            if (isCorrect) moduleStats.correct++;
            moduleStats.accuracy = moduleStats.correct / moduleStats.attempts;

            await setDoc(progressRef, {
                ...existing,
                modules: { ...existing.modules, [moduleId]: moduleStats },
                letters: { ...existing.letters, [letter]: letterStats },
                totalAttempts: existing.totalAttempts + 1,
                totalCorrect: existing.totalCorrect + (isCorrect ? 1 : 0),
                overallAccuracy: (existing.totalCorrect + (isCorrect ? 1 : 0)) / (existing.totalAttempts + 1),
                lastSession: timestamp
            }, { merge: true });

            console.log(`Hangul Attempt Tracked: ${letter} (${isCorrect ? 'Correct' : 'Incorrect'})`);
            return true;
        }, 'track_hangul_attempt', {
            componentName: 'TrackingV2Service',
            severity: 'medium',
            level: 0,
            chapter: 0,
            firebasePath: `feature_testing/Korea_University/Level0/HangulProgress/${data.userId}`
        });
    }
};

export default TrackingV2Service;
